import * as Sentry from '@sentry/nextjs'
import { debug } from '@/utils/debug'
import { playerRoute, eventDetailRoute, contentDetailRoute, myTeamRoute, teamDetailRoute } from '@/utils/routes'
import { toSafeHttpUrl, openExternal } from '@/utils/url'

const CONTENT_TYPE = {
  CONTENT: 'content',
  EVENT: 'event'
} as const

const LINK_TYPE = {
  INTERNAL: 'internal',
  EXTERNAL: 'external'
} as const

export const PLAYLIST_TRIGGER = {
  Checkout: '/checkout'
} as const

// --- Types ---

export interface NavigationContext {
  item: any
  action?: { type?: string; trigger?: string; link?: string }
  videos: any[]
  referrer: string
  /** `false` sends team cards to `/team-detail` instead; absent keeps `/my-team`. */
  hasMyTeamScene?: boolean
}

export type NavigationResult = { url: string; external?: boolean } | null
export type NavigationResolver = (ctx: NavigationContext) => NavigationResult

// --- Resolvers (priority order) ---

export const resolveInternalAction: NavigationResolver = ({ action }) => {
  if (action?.type !== LINK_TYPE.INTERNAL) return null
  const route = action?.trigger && PLAYLIST_TRIGGER[action.trigger as keyof typeof PLAYLIST_TRIGGER]
  return route ? { url: route } : null
}

// `toSafeHttpUrl` now lives in `@/utils/url` so every config-driven
// navigation sink shares one scheme-allowlist validator.

export const resolveExternalAction: NavigationResolver = ({ action }) => {
  if (action?.type !== LINK_TYPE.EXTERNAL || !action?.trigger) return null
  const url = toSafeHttpUrl(action.trigger)
  return url ? { url: url.href, external: true } : null
}

// CMS custom buttons carry a plain `link` URL (no type/trigger) — the shape
// production banners still use. Same-origin links navigate in place so the
// SPA keeps its state; other http(s) URLs open in a new tab. Anything the
// CMS could get wrong (non-http scheme, scheme-less host, malformed URL)
// falls through to the lower-priority resolvers and reports to Sentry —
// this field must never become a javascript:/data: navigation sink.
export const resolveActionLink: NavigationResolver = ({ action }) => {
  if (!action?.link) return null

  // 'www.partner.com/promo' would parse as the in-app path
  // '/www.partner.com/promo' and 404 — only a leading '/' or an explicit
  // scheme is unambiguous.
  const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(action.link)
  if (!action.link.startsWith('/') && !hasScheme) {
    Sentry.captureException(
      new Error(`Ambiguous customButton link (no scheme, not a path): ${action.link}`),
      { level: 'warning' }
    )
    return null
  }

  const url = toSafeHttpUrl(action.link)
  if (!url) return null
  if (url.origin === window.location.origin) {
    return { url: `${url.pathname}${url.search}${url.hash}` }
  }
  return { url: url.href, external: true }
}


export const resolveNavigationLink: NavigationResolver = ({ item }) => {
  const link = item?.navigationOptions?.[0]?.link
  if (!link) return null
  const url = toSafeHttpUrl(link)
  return url ? { url: url.href, external: true } : null
}

export const resolveSingleVideo: NavigationResolver = ({ videos, referrer }) => {
  if (videos.length !== 1) return null
  return { url: playerRoute({ video: videos[0]._id, referrer }) }
}

export const resolveSearchResult: NavigationResolver = ({ item, referrer }) => {
  if (!item?.innerObject || item?._id) return null
  const [_, type, id] = item?.deepLink?.split('/') || []
  if (!id) return null
  const isEvent = type === CONTENT_TYPE.EVENT
  return { url: playerRoute(isEvent ? { event: id, referrer } : { content: id, referrer }) }
}

export const resolveVideoContent: NavigationResolver = ({ item, videos, referrer }) => {
  if (videos.length === 0) return null
  const isEvent = item?._model?.toLowerCase() === CONTENT_TYPE.EVENT
  return { url: playerRoute(isEvent ? { event: item?._id, referrer } : { content: item?._id, referrer }) }
}

export const resolveFallback: NavigationResolver = ({ item, hasMyTeamScene }) => {
  const isEvent = item?._model?.toLowerCase() === CONTENT_TYPE.EVENT
  const isPlaylistEvent = item?.__belong?.Event?._id

  if (isEvent) return { url: eventDetailRoute(item?._id) }
  if (isPlaylistEvent) return { url: eventDetailRoute(item?.__belong?.Event?._id) }
  if (item?.displayDetail) return { url: contentDetailRoute(item?._id) }
  if (item?.flag) return { url: hasMyTeamScene === false ? teamDetailRoute(item?._id) : myTeamRoute(item?._id) }
  return null
}

/**
 * Navigation resolvers, evaluated in priority order.
 * The first resolver to return a non-null result wins.
 *
 * Priority:
 * 1. Internal action (e.g. checkout trigger)
 * 2. External action (opens in new tab)
 * 3. Action link (customButton `link` URL — same-origin in place, rest in new tab)
 * 4. Navigation link override (item-level, opens in new tab)
 * 5. Single video (direct player link)
 * 6. Search result (deep link parsing)
 * 7. Video content (player link for multi-video items)
 * 8. Fallback (event-detail, content-detail, my-team)
 *
 * ⚠️ Order matters: explicit actions take precedence over
 * content-based routing, and video playback takes precedence
 * over detail pages.
 */
const NAVIGATION_RESOLVERS: NavigationResolver[] = [
  resolveInternalAction,
  resolveExternalAction,
  resolveActionLink,
  resolveNavigationLink,
  resolveSingleVideo,
  resolveSearchResult,
  resolveVideoContent,
  resolveFallback,
]

/**
 * Resolves the navigation target for a card click.
 * Evaluates resolvers in priority order — first match wins.
 */
export const resolveNavigation = (ctx: NavigationContext): NavigationResult => {
  for (const resolver of NAVIGATION_RESOLVERS) {
    const result = resolver(ctx)
    if (result) {
      debug.cyan(
        `[Navigation] ${resolver.name} → ${result.url}`,
        result.external ? '(external)' : '(internal)',
        { ctx }
      )
      return result
    }
  }

  debug.yellow.warn('[Navigation] No resolver matched', { ctx })
  return null
}

/**
 * Apply a resolved navigation result: external targets open in a new tab
 * (validated + noopener), internal ones go through the SPA router. No-op on a
 * null result. Shared by every card-click sink so the external-vs-router
 * branch lives in one place.
 */
export const applyNavigation = (
  router: { push: (url: string) => void },
  result: NavigationResult
): void => {
  if (!result) return
  result.external ? openExternal(result.url) : router.push(result.url)
}
